// VIDEO-EDITING — Direct HTTP client for the b-roll propose + apply routes.
//
// These wrap `POST /core/video-editing/broll/propose` and
// `POST /core/video-editing/broll/apply`. The "Auto add B-roll cutaways"
// panel calls these directly instead of going through Marcus chat, so the
// panel works even when the companion isn't mounted.
//
// All responses are Zod-validated at the boundary per frontend/CLAUDE.md
// Gotcha #2.
import { z } from "zod";

import { apiFetch } from "@/lib/jwt-auth-client";

import type {
  BrollCandidate,
  BrollGenerativeProvider,
  BrollProposal,
  BrollStockProvider,
} from "../types/brollProposal";

const BrollCandidateSchema: z.ZodType<BrollCandidate> = z.object({
  id: z.string(),
  source: z.enum(["pexels", "pixabay", "coverr"]) satisfies z.ZodType<BrollStockProvider>,
  sourceUrl: z.string(),
  thumbUrl: z.string(),
  previewUrl: z.string(),
  downloadUrl: z.string(),
  durationSec: z.number(),
  width: z.number(),
  height: z.number(),
  attributionRequired: z.boolean(),
  attributionLine: z.string().nullable(),
});

const BrollProposalSchema: z.ZodType<BrollProposal> = z.object({
  proposalId: z.string(),
  atSec: z.number(),
  durationSec: z.number(),
  phrase: z.string(),
  intent: z.string(),
  mood: z.string(),
  shotType: z.string(),
  searchQuery: z.string(),
  aiPrompt: z.string(),
  defaultSource: z.enum(["stock", "generative"]),
  stockCandidates: z.array(BrollCandidateSchema),
  generativeProvider: z.enum(["seedance", "veo", "higgsfield"]) satisfies z.ZodType<BrollGenerativeProvider>,
  needsProductReference: z.boolean(),
  referenceMode: z.enum(["none", "soft", "strong"]),
  avoidVisuals: z.string(),
  negativePrompt: z.string(),
});

const ProposeResponseSchema = z.object({
  action: z.literal("video_broll_proposals_ready"),
  projectId: z.string(),
  sourceActionId: z.string(),
  policy: z.literal("replaceSourceAction"),
  proposals: z.array(BrollProposalSchema),
  proposalCount: z.number(),
  durationSec: z.number(),
  aspect: z.enum(["9:16", "16:9", "1:1"]),
  orientation: z.enum(["portrait", "landscape", "square"]),
  productRefAttached: z.boolean(),
  note: z.string().optional(),
});

// An applied b-roll OVERLAY (the apply service now returns BrollClip[], not
// spliced clips). Loose like the boundary schemas elsewhere — passthrough so
// a BE field addition doesn't reject the whole response.
const AppliedBrollSchema = z
  .object({
    id: z.string(),
    url: z.string(),
    storageKey: z.string().optional(),
    mimeType: z.string().optional(),
    startSec: z.number(),
    endSec: z.number(),
    trimStartSec: z.number(),
    trimEndSec: z.number(),
    assetDurationSec: z.number().optional(),
    muted: z.boolean().optional(),
    transitionSec: z.number().optional(),
  })
  .passthrough();

const ApplyResponseSchema = z.object({
  action: z.literal("video_broll_upserted"),
  projectId: z.string(),
  sourceActionId: z.string(),
  addedClipCount: z.number(),
  addedClipIds: z.array(z.string()),
  addedClips: z.array(AppliedBrollSchema),
  replacedClipCount: z.number(),
  replacedClipIds: z.array(z.string()),
  durationSec: z.number().nullable(),
  brolls: z.array(AppliedBrollSchema).optional(),
});

export type ProposeBrollResponse = z.infer<typeof ProposeResponseSchema>;
export type ApplyBrollResponse = z.infer<typeof ApplyResponseSchema>;

export interface ProposeBrollRequest {
  projectId: string;
  sourceActionId?: string;
  maxMoments?: number;
  includeStock?: boolean;
}

export type ApplyBrollProposalSource = "stock" | "generative" | "upload";

export interface ApplyBrollProposalInput {
  proposalId: string;
  atSec: number;
  durationSec: number;
  source: ApplyBrollProposalSource;
  clipUrl?: string;
  aiPrompt?: string;
  generativeProvider?: BrollGenerativeProvider;
  shotType?: string;
  avoidVisuals?: string;
  negativePrompt?: string;
  attributionLine?: string | null;
}

export interface ApplyBrollRequest {
  projectId: string;
  sourceActionId: string;
  proposals: ApplyBrollProposalInput[];
}

async function jsonOrThrow<T>(res: Response, schema: z.ZodType<T>): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg =
      (body as { error?: string }).error ?? `Request failed (${res.status})`;
    throw new Error(msg);
  }
  const data: unknown = await res.json();
  return schema.parse(data);
}

export async function proposeBroll(
  body: ProposeBrollRequest,
  signal?: AbortSignal,
): Promise<ProposeBrollResponse> {
  const res = await apiFetch("/core/video-editing/broll/propose", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  });
  return jsonOrThrow(res, ProposeResponseSchema);
}

export async function applyBroll(
  body: ApplyBrollRequest,
  signal?: AbortSignal,
): Promise<ApplyBrollResponse> {
  const res = await apiFetch("/core/video-editing/broll/apply", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  });
  return jsonOrThrow(res, ApplyResponseSchema);
}
